/**
 * TRIL-393: single source of truth for all user-facing vouch strings.
 * Copy changes often and is edited by non-engineers — keep it here, referenced,
 * never inlined in JSX.
 */
export const VOUCH_COPY = {
  pageTitle: 'Vouching',
  searchHeading: 'Vouch for someone',
  searchPlaceholder: 'Search for a user by name…',
  searchLabel: 'Search for a user to vouch for',
  searchEmpty: 'No users match that search.',
  creditsLabel: 'Credits to stake',
  confirmTerms:
    "Boosts this person's reach for 60 days. If they're moderated for a violation during the window, your staked credits are burned. If they thrive, the credits return to you.",
  confirmTarget: (username: string) => `Vouching for @${username}`,
  confirmLoading: 'Vouching…',
  confirmDone: 'Done',
  confirmButton: 'Vouch',
  confirmSuccess: (boost: number) =>
    `Boosting their reach by ${boost} for 60 days.`,
  lockedHeading: 'No credits to lend yet',
  lockedNoCredits:
    "You don't have any vouching credits yet. Credits come from reputation you've earned above the floor (60) — keep publishing to build yours, then you can back others.",
  lockedCommitted: (n: number) =>
    `Your ${n} vouching ${n === 1 ? 'credit is' : 'credits are'} committed to active vouches. They'll return to you when those resolve — earn more reputation to lend more.`,
  givenHeading: "People you've vouched for",
  givenEmpty: "You haven't vouched for anyone yet.",
  receivedHeading: "Vouches you've received",
  receivedLead: (n: number) =>
    `You have ${n} active ${n === 1 ? 'vouch' : 'vouches'} — someone's backing your reach.`,
  receivedEmpty: 'No active vouches yet.',
  receivedGrow: "Keep building — soon you'll be able to vouch for others too.",
  toastReceivedTitle: 'New vouch',
  toastReceivedBody: (sharerUsername: string, credits: number) => {
    const who = sharerUsername ? `@${sharerUsername}` : 'Someone';
    const unit = credits === 1 ? 'credit' : 'credits';
    return `${who} vouched for you with ${credits} ${unit}.`;
  },
  blurb: {
    // Short orientation only — the full lifecycle lives at /readme/vouching
    // (TRIL-422). Keep just the decision-relevant co-signer caveat here, since
    // this renders where a user is about to stake credits.
    title: 'Vouching in short',
    body: "Vouching lends a slice of your earned reputation to boost someone's reach for 60 days. It costs nothing upfront, but you're a co-signer: if they're moderated in that window, your staked credits are burned.",
    // Arrow is rendered separately as an aria-hidden span so it isn't read as
    // "right arrow" in the link's accessible name.
    fullGuide: 'Read the full guide',
  },
  revokeAction: 'Revoke',
  revokeConfirm: (username: string) =>
    `Revoking ends @${username}'s reach boost now. Your staked credits stay committed and can still burn if they're moderated before the 60-day window closes.`,
  revokeConfirmButton: 'Revoke vouch',
  revokeKeep: 'Keep',
  revokePending: 'Revoking…',
  revokeWinding: 'Revoked — winding down',
  revokeWindingNote:
    "“Revoked — winding down” means you ended the boost, but your staked credits stay committed until that vouch's 60-day window closes — then they return to you (or burn if the person is moderated first). You can vouch for that person again anytime with your available credits.",
  errors: {
    SELF_VOUCH: "You can't vouch for yourself.",
    DIRECT_RECIPROCAL: "You've already got an active vouch with this person.",
    CONCURRENCY_CAP: "You've reached the maximum number of active vouches.",
    INSUFFICIENT_CREDITS: "You don't have enough available credits for that.",
    RECIPIENT_NOT_FOUND: "We couldn't find that user.",
    VOUCHING_DISABLED: 'Vouching is not available right now.',
    ALREADY_VOUCHING:
      'You already vouch for this person — manage it in your roster below.',
    VOUCH_NOT_REVOCABLE: 'This vouch can no longer be revoked.',
    fallback: "Something went wrong — your vouch wasn't created.",
  },
} as const;

export function vouchErrorMessage(code: string): string {
  const map = VOUCH_COPY.errors as Record<string, string>;
  return map[code] ?? VOUCH_COPY.errors.fallback;
}
